import { useEffect, useState } from "react";
import "../styles/homepage.css";
import Footer from "../components/footer.js";
import Logo from "../resources/images/RIYU_LOGO.png";
import Phones from "../resources/images/Phones.png";
import ArrowIcon from "../resources/images/email-icon.png";
import AxisArrowIcon from "../resources/svg/ticks.svg";
import ClockFastIcon from "../resources/svg/time.svg";
import ShoppingFavoriteIcon from "../resources/svg/shop.svg";
import TargetArrowIcon from "../resources/svg/target.svg";
import Check from "../resources/svg/check.svg";
import Boarding from "../resources/images/Boarding.png";
import Chat from "../resources/images/Chat.png";
import Weekplan from "../resources/images/Weekplan.png";
const introCards = [
  {
    title: "Es lernt deinen Alltag",
    text: "Ziele, Vorlieben, Allergien und Zeitbudget – einmal erfassen, laufend verfeinern.",
  },
  {
    title: "Es passt sich an",
    text: "Feedback wie 'Mag ich' und 'Nicht zeigen' verändert Empfehlungen sofort.",
  },
  {
    title: "Es denkt systemisch",
    text: "Rezepte, Ziele und Einkaufsliste greifen nahtlos ineinander.",
  },
];

const processSteps = [
  {
    title: "Onboarding",
    text: "Ziele, Vorlieben, No-Gos, Zeitbudget – in 3 Minuten.",
    source: Boarding,
  },
  {
    title: "Rezept + Nährwerte",
    text: "Mila lernt dich kennen und lernt fortlaufend dazu",
    source: Chat,
  },
  {
    title: "Wochenplan + Einkaufsliste",
    text: "Alltagstauglich, abwechslungsreich, anpassbar",
    source: Weekplan,
  },
];

const benefitCards = [
  {
    title: "Weniger Entscheidungen",
    icon: AxisArrowIcon,
    text: "Du musst nicht täglich neu planen.",
  },
  {
    title: "Zeit sparen",
    icon: ClockFastIcon,
    text: "Wochenplanung in wenigen Minuten.",
  },
  {
    title: "Einkauf, der passt",
    icon: ShoppingFavoriteIcon,
    text: "Liste nach Haushalt & Plan.",
  },
  {
    title: "Ziele effektiv erreichen",
    icon: TargetArrowIcon,
    text: "Struktur statt Diätstress.",
  },
];

const faqItems = [
  {
    question: "Wie bekomme ich Zugang?",
    answer: "Aktuell per Einladung nach Warteliste. Du bekommst eine E-Mail, sobald ein Platz frei ist.",
  },
  {
    question: "Wann startet RIYU?",
    answer: "Wir rollen in kleinen Cohorts aus. Wenn du auf der Warteliste bist, informieren wir dich zuerst..",
  },
  {
    question: "Ist RIYU kostenlos?",
    answer: "Der Preview-Zugang ist limitiert. Preismodell und Pläne kommunizieren wir transparent vor dem Start.",
  },
  {
    question: "Welche Daten braucht RIYU?",
    answer: "Nur das, was nötig ist: Ziele, Vorlieben und optional Allergien. Du kannst Daten löschen oder exportieren.",
  },
  {
    question: "Ersetzt RIYU eine medizinische Beratung?",
    answer: "Nein. RIYU unterstützt deine Ernährung im Alltag, ersetzt aber keine ärztliche Diagnose oder Therapie.",
  },
];

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [heroEmail, setHeroEmail] = useState("");
  const [heroAgbAccepted, setHeroAgbAccepted] = useState(false);
  const [heroSubmitted, setHeroSubmitted] = useState(false);
  const [heroError, setHeroError] = useState("");
  const [heroLoading, setHeroLoading] = useState(false);
  const showHeroAgb = heroEmail.trim().length > 0 && !heroSubmitted;
  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 24);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const frameId = window.requestAnimationFrame(() => setIsLoaded(true));
    return () => window.cancelAnimationFrame(frameId);
  }, []);

  const handleHeroEmailChange = (event) => {
    const nextEmail = event.target.value;
    setHeroEmail(nextEmail);
    setHeroSubmitted(false);
    setHeroError("");

    if (!nextEmail.trim()) {
      setHeroAgbAccepted(false);
    }
  };

  const handleHeroSubmit = async (event) => {
    event.preventDefault();
    setHeroError("");
    setHeroSubmitted(false);

    if (!validateEmail(heroEmail)) {
      setHeroError("Bitte gib eine gueltige E-Mail-Adresse ein.");
      return;
    }

    if (!heroAgbAccepted) {
      setHeroError("Bitte stimme den AGB zu.");
      return;
    }

    try {
      setHeroLoading(true);
      const response = await fetch("/api/waitlist-register", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: heroEmail,
          register: true,
          consent: heroAgbAccepted,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to subscribe");
      }

      setHeroSubmitted(true);
      setHeroEmail("");
      setHeroAgbAccepted(false);
    } catch (error) {
      console.error(error);
      setHeroError("Es gab ein Problem beim Anmelden. Bitte versuche es erneut.");
    } finally {
      setHeroLoading(false);
    }
  };

  return (
    <div
      className={`homepage${isScrolled ? " homepage--scrolled" : ""}${
        isLoaded ? " homepage--loaded" : ""
      }`}
    >
      <div className="homepage__canvas" aria-hidden="true" />

      <main className="homepage__container">
        <header className="home-header">
          <img className="home-logo-mark" src={Logo} alt="RIYU Logo" />
          <span className="home-logo-text"> RIYU</span>
        </header>

        <section className="hero" aria-labelledby="hero-title">
          <div className="hero__content">
            <h1 id="hero-title" className="hero__title">
              Dein
              <br />
              Personal
              <br />
              Nutrition OS
            </h1>
            <p className="hero__description">
              Hyperpersonalisierte Rezepte, Wochenpläne & Einkaufsliste –
              passend zu deinen Zielen und deinem Alltag.
            </p>
            <div className="hero__form-container">
              <form
                className="hero__form"
                onSubmit={handleHeroSubmit}
                noValidate
              >
                
                <input
                  type="email"
                  placeholder="E-Mail für Einladung"
                  aria-label="E-Mail Adresse"
                  inputMode="email"
                  autoComplete="email"
                  value={heroEmail}
                  onChange={handleHeroEmailChange}
                  aria-invalid={Boolean(heroError) && !validateEmail(heroEmail)}
                />
                <button
                  type="submit"
                  aria-label="Zur Warteliste"
                  disabled={heroLoading}
                >
                  <img src={ArrowIcon} className="email-icon" alt="Pfeil" />
                </button>
                {showHeroAgb ? (
                  <label className="hero__consent">
                    <input
                      type="checkbox"
                      checked={heroAgbAccepted}
                      onChange={(event) => setHeroAgbAccepted(event.target.checked)}
                    />
                    <span>
                      Ich habe die <a href="/agb">AGB</a> gelesen und stimme zu.
                    </span>
                  </label>
                ) : null}
              </form>
              {heroError ? (
                <p className="hero__form-status hero__form-status--error" role="alert">
                  {heroError}
                </p>
              ) : null}
              {heroSubmitted ? (
                <p className="hero__form-status hero__form-status--success">
                  Danke! Bitte prüfe dein Postfach.
                </p>
              ) : null}
              <p className="hero__form-note">
                Zugang begrenzt - Vergabe nach Warteliste
              </p>
            </div>
          </div>
            <img
              src={Phones}
              className="hero__mockup"
              alt="Mockup von RIYU auf zwei Handys"
            />
         
        </section>

        <section className="section intro" aria-labelledby="intro-title">
          <h2 id="intro-title" className="section__title">
            Was ist ein Personal Nutrition OS?
          </h2>
          <p className="section__lead">
           Das Betriebsystem für deinen Alltag - Ein System, dass Zutaten, Ziele und Einkauf intelligent verbindet
          </p>

          <div className="feature-grid">
            {introCards.map((card) => (
              <article className="feature-card" key={card.title}>
                <div className="titles">{card.title}</div>
                <p>{card.text}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="section process" aria-labelledby="process-title">
          <h2 id="process-title" className="section__title">
            So funktioniert es
          </h2>
          <p className="section__lead">
            In drei klaren Schritten entsteht ein System, das sich laufend an
            dich anpasst.
          </p>
          <div className="process__mockups" aria-hidden="true">
            {processSteps.map((step, index) => (
              <article className="mockup-card" key={`mockup-${step.title}`}>
                <div className="mockup-card__text">
                  <span className="process-step__number">{index + 1}</span>
                  <h3>{step.title}</h3>
                  <p>{step.text}</p>
                </div>
                <img
                  src={step.source}
                  className="mockup-card__image"
                  alt={`Mockup für Schritt: ${step.title}`}
                />
              </article>
            ))}
          </div>

          <div className="process__cta">
            <button onClick={() => document.getElementById("hero-title").scrollIntoView({ behavior: "smooth" })} className="button">Jetzt auf die Warteliste</button>
            <p>Deine Daten bleiben in der EU</p>
          </div>
        </section>

        <section className="section benefits" aria-labelledby="benefits-title">
          <div className="benefits__intro">
            <h2 id="benefits-title" className="section__title">
              Was RIYU für dich verändert
            </h2>
            <p className="section__lead">
              Weniger Entscheidungen. Bessere Ernährung.
            </p>
          </div>

          <div className="benefits__grid">
            {benefitCards.map((card) => (
              <article className="benefit-card" key={card.title}>
                <span className="benefit-card__icon" aria-hidden="true">
                  <img src={card.icon} alt="" />
                </span>
                <h3>{card.title}</h3>
                <p>{card.text}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="section section_audience" aria-labelledby="audience-title">
          <h2 id="audience-title" className="section__title">
            Für wen ist RIYU?
          </h2>
          <p className="section__lead">
          Für Menschen, die Ernährung zur Priorität machen – ohne Diätstress.
          </p>

          <div className="audience__columns">
            <div className="audience__left">
              <ul>
                <li>Familien / Haushalte, die Planung & Einkauf vereinfachen wollen</li>
                <li>Berufstätige mit wenig Zeit, aber hohen Ansprüchen</li>
                <li>Sportlich Aktive, die Performance & Regeneration optimieren</li>
                <li>Menschen mit klaren Zielen (Fokus, Gewicht, Kraft, Routine)</li>
                <li>Alle, die personalisierte Pläne statt generischer Rezepte wollen</li>
              </ul>

            </div>

            <aside className="audience__card">
              <h3 className="titles">Passt zu dir, wenn …</h3>
              <ul>
                <li><img src={Check} alt="" aria-hidden="true" />du Struktur willst, aber flexibel bleiben möchtest</li>
                <li><img src={Check} alt="" aria-hidden="true" />du Rezepte + Plan + Einkauf in einem System willst</li>
                <li><img src={Check} alt="" aria-hidden="true" />du personalisierte Vorschläge statt Standardpläne erwartest</li>
              </ul>
            </aside>
          </div>
        </section>

        <section className="section trust" aria-labelledby="trust-title">
          <div className="trust__panel">
            <div className="trust__intro">
              <h2 id="trust-title" className="section__title">
              Vertrauen und Klarheit
              </h2>
              <div className="trust__intro-copy">
              <p>RIYU ist ein lernendes System - kein Diätprogramm.</p>
              <p>Deine Daten werden ausschließlich in der EU verarbeitet.</p>
              <p>Du kannst deine Daten jederzeit löschen lassen.</p>
              <p>RIYU ersetzt keine medizinische Beratung.</p>
              </div>
            </div>

            <div className="trust__cards">
              <article className="trust__card trust__card--is">
                <p className="trust__label">RIYU ist ...</p>
                <ul className="trust__list">
                  <li>Wissenschaft und Daten getrieben</li>
                  <li>Kontrolle über deine Nährstoffe</li>
                  <li>Zielorientierte Ernährung die zu dir passt</li>
                </ul>
              </article>
              <article className="trust__card trust__card--not">
                <p className="trust__label">RIYU ist ...</p>
                <ul className="trust__list">
                  <li>kein statischer Ernährungsplan</li>
                  <li>kein reiner Kalorienzähler</li>
                </ul>
              </article>
            </div>
          </div>
        </section>

        <section className="section faq" aria-labelledby="faq-title">
          <div className="faq__intro">
            <h2 id="faq-title" className="section__title">
              Häufige Fragen
            </h2>
            <p className="section__lead">
              Die wichtigsten Antworten für dich.
            </p>
          </div>

          <div className="faq__items">
            {faqItems.map((item, index) => (
              <details
                className="faq-item"
                key={item.question}
                open={index === 0}
              >
                <summary>{item.question}</summary>
                <p>{item.answer}</p>
              </details>
            ))}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
